// The public sign-up page and the free trial (laravel SignUpController, App/Trial.php), in one place so they can be
// tested. The server checks everything again and has the last word.
import { dialCodeFor, toInternational, phoneProblem } from './phone'
import { newPasswordProblem } from './passwords'
import { MAX_FIELD_LENGTH, isFieldTooLong } from './fieldLimits'
import { looksLikeEmail } from './signin'

const blank = (value) => value == null || String(value).trim() === ''

// Who the account is for (laravel App\PrivateAccount): a security company, or a person or household employing their
// own guards. The second is only offered while a private Terms and Conditions version is published (signup/info's
// private_terms).
export const SECURITY_COMPANY = 'company'
export const MYSELF = 'private'

export const ACCOUNT_CHOICES = [
	{ value: SECURITY_COMPANY, label: 'A security company' },
	{ value: MYSELF, label: 'A private individual with your own guards' },
]

// Whether the page offers the "A private individual with your own guards" choice
export function offersPrivate(info) {
	return !!(info && info.private_terms)
}

// The Terms and Conditions for the chosen type ({id, version, url}), or null
export function termsFor(info, accountType) {
	if (!info) {
		return null
	}
	return accountType === MYSELF ? info.private_terms || null : info.terms || null
}

// What is wrong with the sign-up form, or null. form: { accountType, companyName, country, timezone, zoneNeeded,
// firstName, lastName, email, phone, password, passwordAgain, agreed }. A private account has no company name.
export function signupProblem(form) {
	const isPrivate = form.accountType === MYSELF
	if (!isPrivate && blank(form.companyName)) {
		return 'Please enter your company name.'
	}
	if ((!isPrivate && isFieldTooLong(form.companyName)) || isFieldTooLong(form.firstName) || isFieldTooLong(form.lastName)) {
		return 'Names must be ' + MAX_FIELD_LENGTH + ' characters or fewer.'
	}
	if (blank(form.country)) {
		return 'Please choose your country.'
	}
	if (form.zoneNeeded && blank(form.timezone)) {
		return 'Please choose your time zone: your country has several.'
	}
	if (blank(form.firstName) || blank(form.lastName)) {
		return 'Please enter your first and last name.'
	}
	if (blank(form.email) || !looksLikeEmail(form.email)) {
		return 'Please enter a valid email address.'
	}
	if (blank(form.phone)) {
		return 'Please enter a phone number.'
	}
	const dialCode = dialCodeFor(form.country)
	if (!toInternational(form.phone, dialCode)) {
		return phoneProblem(form.phone, dialCode)
	}
	const passwordProblem = newPasswordProblem(form.password, form.passwordAgain)
	if (passwordProblem) {
		return passwordProblem.replace('The new password', 'The password')
	}
	if (!form.agreed) {
		return 'Please tick the box to agree to the Terms and Conditions.'
	}
	return null
}

// The request for signup/register. website: the hidden field (always empty for a person). termsVersionId: the version
// for the chosen type.
export function signupRequest(form, termsVersionId, website) {
	const isPrivate = form.accountType === MYSELF
	return {
		account_type: isPrivate ? MYSELF : SECURITY_COMPANY,
		company_name: isPrivate ? null : String(form.companyName).trim(),
		countrycode: form.country,
		timezone: form.timezone || null,
		first_name: String(form.firstName).trim(),
		last_name: String(form.lastName).trim(),
		email: String(form.email).trim(),
		phonenumber: toInternational(form.phone, dialCodeFor(form.country)),
		password: form.password,
		password_confirmation: form.passwordAgain,
		terms_accepted: !!form.agreed,
		terms_version_id: termsVersionId,
		website: website || ''
	}
}

// The banner of a company in its free trial (billing/notice's trial): "Free trial: ends 1 December 2026, up to 2 guards"
export function trialBannerText(trial) {
	if (!trial || !trial.ends_on_label) {
		return null
	}
	return 'Free trial: ends ' + trial.ends_on_label + ', up to ' + trial.max_guards + ' guards'
}

// The super admin's Billing page: a company's trial as a short badge, or null when it has none
export function trialBadge(trial, shortDate) {
	if (!trial) {
		return null
	}
	return trial.active
		? { label: 'Trial: ends ' + shortDate(trial.ends_on), tone: 'info', title: 'Free trial until ' + trial.ends_on_label + ' (at most 2 guards); billing from the day after' }
		: { label: 'Trial ended', tone: 'secondary', title: 'The free trial ended on ' + trial.ends_on_label }
}

// "Version 2026-11, accepted 1 November 2026 by ..." on the super admin's company page (auth/company/get's terms_acceptance)
export function termsAcceptanceText(acceptance, formatDate) {
	if (!acceptance) {
		return 'Not accepted yet'
	}
	return 'Version ' + acceptance.version + (acceptance.current ? '' : ' (not the current one)') + ', accepted '
		+ formatDate(acceptance.accepted_at) + ' by ' + acceptance.by
}
